
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import ShowProfitToggle from '@/components/ShowProfitToggle.vue';
import settingx from '@/store/modules/settingx';
import { roundToFixed } from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import {getTableName} from '@/worker/db/sqlHelpers';

@Component({
	components: {
		AmountDisplayerFilterable,
		ShowProfitToggle,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class CustomerYtdGrossProfitNumberDisplayer extends FilterWidget {
	@Prop({
		default: () => [
			moment().startOf('year').valueOf(),
			moment().endOf('month').valueOf(),
		],
	})
	public selectedYearToDateRange!: [number, number];

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public profitPercentage = 0;
	public profitAmount = '' || 0;

	public get permissionIds(): PermissionsGroup[] {
		return ['customers', 'sales'];
	}

	public get showProfit() {
		return settingx.now.showProfit;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get title() {
		return this.showProfit ? 'YTD Gross Profit' : 'YTD Gross Margin';
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedYearToDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			selectedCustomers,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedYearToDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			selectedCustomers,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);
		const jobId = this.currentJobId;
		const YTDThisYear = this.selectedYearToDateRange;

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}

		const [dateStart, dateEnd] = YTDThisYear;
		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');
		const docTypes = this.includeDebitNote ? '\'IV\',\'CS\',\'DN\'' : '\'IV\',\'CS\'';

		const tablesToLoad: string[] = ['sales', 'creditNotes'];
		if (!this.slPhModuleOnly) {
			tablesToLoad.push('customerTrades');
		}
		const queryHead = await this._ensureTablesLoaded(tablesToLoad as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		// SL: get amount and profit from sales + credit notes
		const [slSalesResult, slCNResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total_amount,
					SUM(TRY_CAST("PROFIT" AS DOUBLE)) as total_profit
				FROM ${salesTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
					AND "DOCTYPE" IN (${docTypes})
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total_amount,
					SUM(TRY_CAST("PROFIT" AS DOUBLE)) as total_profit
				FROM ${cnTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
					${customerFilter}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.5, 0.7);

		const slSalesAmount = Number(slSalesResult[0] && slSalesResult[0].total_amount) || 0;
		const slSalesProfit = Number(slSalesResult[0] && slSalesResult[0].total_profit) || 0;
		const slCNAmount = Number(slCNResult[0] && slCNResult[0].total_amount) || 0;
		const slCNProfit = Number(slCNResult[0] && slCNResult[0].total_profit) || 0;

		const slAmount = slSalesAmount - (this.includeCreditNote ? slCNAmount : 0);
		const slProfit = slSalesProfit - (this.includeCreditNote ? slCNProfit : 0);

		let salesAmount: number;
		let salesProfit: number;

		if (this.slPhModuleOnly) {
			salesAmount = slAmount;
			salesProfit = slProfit;
		} else {
			const arResult = await queryHead.rawQuery(`
				SELECT
					SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as iv_amt,
					SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as cn_amt,
					SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as dn_amt
				FROM ${arTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
					${customerFilter}
			`);
			this.checkInterrupted(jobId);

			const arIv = Number(arResult[0] && arResult[0].iv_amt) || 0;
			const arCN = Number(arResult[0] && arResult[0].cn_amt) || 0;
			const arDN = Number(arResult[0] && arResult[0].dn_amt) || 0;

			const arAmount = arIv
				- (this.includeCreditNote ? arCN : 0)
				+ (this.includeDebitNote ? arDN : 0);

			const extraProfit = arAmount - slAmount;
			salesAmount = arAmount;
			salesProfit = slProfit + extraProfit;
		}

		this.profitAmount = roundToFixed(salesProfit, this.decimal);

		this.profitPercentage = roundToFixed(
			salesAmount !== 0 ? (salesProfit / salesAmount) * 100 : 0,
			this.decimal,
		);

		this.generateLoadingText(1);
		this.saveHistory('profitPercentage', 'profitAmount');
	}
}
